import type { ReactNode } from 'react';
import { memo, useMemo } from 'react';
import type { Placement } from 'react-aria';
import type { TooltipProps } from '@launchpad-ui/components';
import { Button, DialogTrigger, Popover, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import clsx from 'clsx';

import styles from './multiItem.module.css';

function MultiItemRenderer<T, V extends Variant = Variant>({
  list,
  variant,
  max = 2,
  fallback,
  triggerClassName,
  renderItem,
  overflowIndicator,
  toolTipOverflow,
  toolTipChildren,
  toolTipClassName,
  behavior = 'tooltip' as V,
  placement = 'bottom',
}: Props<T, V>) {
  const classes = clsx(styles.container, triggerClassName);
  const toolTipClasses = clsx(styles.toolTip, styles[behavior], toolTipClassName);
  const firstN = useMemo(() => list.slice(0, max).map(renderItem), [list, max, renderItem]);
  const additional = list.length - firstN.length;
  const overflow = overflowIndicator ? overflowIndicator(additional) : `+${additional} more`;
  const [Trigger, Expand] = behavior === 'popover' ? [DialogTrigger, Popover] : [TooltipTrigger, Tooltip];

  if (!firstN.length) {
    return fallback;
  }

  if (!additional && toolTipOverflow) {
    return (
      <div className={classes}>
        <div>{firstN}</div>
      </div>
    );
  }

  if (behavior === 'popover' && additional > 0) {
    return (
      <span className={clsx(styles.popoverContainer, triggerClassName)}>
        {firstN}
        <Trigger>
          <Button className={styles.container}>
            {!!additional && <span className={styles.more}>{overflow}</span>}
          </Button>
          {toolTipChildren && (
            <Expand variant={variant} className={toolTipClasses} placement={placement}>
              {toolTipChildren}
            </Expand>
          )}
        </Trigger>
      </span>
    );
  }

  return (
    <Trigger>
      <Button className={classes}>
        <div>{firstN}</div>
        {!!additional && <span className={styles.more}>{overflow}</span>}
      </Button>
      {toolTipChildren && (
        <Expand variant={variant} className={toolTipClasses} placement="bottom">
          {toolTipChildren}
        </Expand>
      )}
    </Trigger>
  );
}

export const MultiItemDisplay = memo(MultiItemRenderer) as typeof MultiItemRenderer;

export interface Props<T, V extends Variant = Variant> {
  list: T[];
  max?: number;
  behavior?: V;
  fallback: ReactNode;
  toolTipClassName?: string;
  toolTipOverflow?: boolean;
  triggerClassName?: string;
  toolTipChildren: ReactNode;
  overflowIndicator?: (n: number) => ReactNode;
  renderItem: (item: T, index: number, list: T[]) => ReactNode;
  variant?: V extends 'tooltip' ? TooltipProps['variant'] : undefined;
  placement?: Placement;
}

export type Variant = 'tooltip' | 'popover';
